import { Product, Order, SiteConfig, LandingContent } from '../types';
import { supabase } from './supabaseClient';

const TABLE_PRODUCTS = 'products';
const PUBLIC_PRODUCT_COLUMNS = 'id, title, description, price, "originalPrice", "isOnSale", "saleEndsAt", "isNew", categories, gallery, features, tags, "coverImage", "demoVideoUrl", "is18Plus", created_at';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function mapAuthError(error: { message?: string; status?: number; code?: string } | null): string {
  if (!error) return 'Unknown authentication error';

  const message = error.message || 'Authentication failed';
  const normalized = message.toLowerCase();

  if (normalized.includes('invalid login credentials')) {
    return 'Wrong email or password';
  }

  if (normalized.includes('email not confirmed')) {
    return 'Email is not confirmed';
  }

  if (normalized.includes('email provider is disabled')) {
    return 'Email sign-in is disabled in Supabase';
  }

  return message;
}

function mapProduct(item: any, includeSecrets = false): Product {
  return {
    id: item.id,
    title: item.title,
    description: item.description || '',
    price: Number(item.price),
    originalPrice: item.originalPrice ? Number(item.originalPrice) : undefined,
    isOnSale: !!item.isOnSale,
    saleEndsAt: item.saleEndsAt || undefined,
    isNew: !!item.isNew,
    categories: Array.isArray(item.categories) ? item.categories : [],
    coverImage: item.coverImage || '',
    gallery: Array.isArray(item.gallery) ? item.gallery : [],
    demoVideoUrl: item.demoVideoUrl || undefined,
    digitalAssetUrl: includeSecrets ? (item.digitalAssetUrl || undefined) : undefined,
    digitalAssetInstructions: includeSecrets ? (item.digitalAssetInstructions || undefined) : undefined,
    is18Plus: !!item.is18Plus,
    features: Array.isArray(item.features) ? item.features : [],
    tags: Array.isArray(item.tags) ? item.tags : []
  };
}

export interface DownloadLink {
  id: string;
  title: string;
  digitalAssetUrl: string;
  digitalAssetInstructions?: string;
}

export const DB = {
  // =============================================
  // AUTH (Supabase Auth)
  // =============================================

  login: async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const normalizedEmail = normalizeEmail(email);
    const normalizedPassword = password.trim();

    if (!normalizedEmail || !normalizedPassword) {
      return { success: false, error: 'Enter email and password' };
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: normalizedPassword,
      });

      if (error) {
        return { success: false, error: mapAuthError(error) };
      }
      return { success: !!data.session };
    } catch (e) {
      console.error('[Auth] Login exception:', e);
      return { success: false, error: 'Connection error' };
    }
  },

  checkAuth: async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session;
    } catch {
      return false;
    }
  },

  logout: async (): Promise<void> => {
    await supabase.auth.signOut();
  },

  // =============================================
  // CONFIG / CMS
  // =============================================

  getSiteConfig: async (): Promise<SiteConfig> => {
    try {
      const { data, error } = await supabase.from('site_config').select('*');
      const config: SiteConfig = { heroVideos: [], featuredProductIds: [] };
      if (error || !data) return config;

      data.forEach(row => {
        if (row.key === 'hero_videos') {
          config.heroVideos = (row.value as any[]).map(item => ({
            ...item,
            type: item.type || 'video',
            isNSFW: !!item.isNSFW
          }));
        }
        if (row.key === 'featured_ids') config.featuredProductIds = row.value;
        if (row.key === 'landing_content' && row.value && typeof row.value === 'object') config.landingContent = row.value as LandingContent;
      });
      return config;
    } catch (e) {
      console.error("Config Exception", e);
      return { heroVideos: [], featuredProductIds: [] };
    }
  },

  updateSiteConfig: async (key: 'hero_videos' | 'featured_ids' | 'landing_content', value: any): Promise<boolean> => {
    try {
      const { error } = await supabase.from('site_config').upsert({ key, value });
      return !error;
    } catch {
      return false;
    }
  },

  // =============================================
  // ORDERS
  // =============================================

  createOrder: async (orderData: Order): Promise<boolean> => {
    try {
      const { error } = await supabase.from('orders').insert([{
        invoiceId: orderData.invoiceId,
        status: orderData.status,
        totalAmount: orderData.totalAmount,
        currency: orderData.currency,
        items: orderData.items
      }]);
      if (error) { console.error("Order Error:", error.message); return false; }
      return true;
    } catch { return false; }
  },

  getAllOrders: async (): Promise<Order[]> => {
    try {
      const { data, error } = await supabase
        .from('orders').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((item: any) => ({
        id: item.id,
        invoiceId: item.invoiceId,
        status: item.status,
        totalAmount: Number(item.totalAmount),
        currency: item.currency,
        items: item.items,
        createdAt: item.created_at
      }));
    } catch { return []; }
  },

  updateOrderStatus: async (invoiceId: number, status: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('orders').update({ status }).eq('invoiceId', invoiceId);
      return !error;
    } catch { return false; }
  },

  getNextInvoiceId: async (): Promise<number> => {
    try {
      const { data, error } = await supabase.from('orders').select('invoiceId').order('invoiceId', { ascending: false }).limit(1);
      if (error || !data?.length) return 9000000000 + Math.floor(Date.now() % 1000000000);
      return Math.max(Number(data[0].invoiceId) + 1, 9000000000);
    } catch { return 9000000000 + Math.floor(Date.now() % 1000000000); }
  },

  updateOrder: async (orderId: string, payload: Partial<Order>): Promise<boolean> => {
    try {
      const { error } = await supabase.from('orders').update({
        invoiceId: payload.invoiceId,
        status: payload.status,
        totalAmount: payload.totalAmount,
        currency: payload.currency,
        items: payload.items
      }).eq('id', orderId);
      return !error;
    } catch { return false; }
  },

  deleteOrder: async (orderId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('orders').delete().eq('id', orderId);
      return !error;
    } catch { return false; }
  },

  // =============================================
  // PRODUCTS — PUBLIC (no secret fields)
  // =============================================

  getProducts: async (): Promise<Product[]> => {
    try {
      const { data, error } = await supabase.rpc('get_public_products');
      if (!error && data) {
        return (data as any[]).map(item => mapProduct(item));
      }

      if (error) {
        console.warn('get_public_products RPC unavailable, using safe fallback:', error.message);
      }

      const { data: fallback, error: fbErr } = await supabase
        .from(TABLE_PRODUCTS)
        .select(PUBLIC_PRODUCT_COLUMNS)
        .order('created_at', { ascending: false });

      if (fbErr) throw fbErr;
      return (fallback || []).map(item => mapProduct(item));
    } catch (e) {
      console.error("Products fetch error:", e);
      return [];
    }
  },

  // =============================================
  // PRODUCTS — ADMIN (reads from TABLE, all columns)
  // =============================================

  getProductsAdmin: async (): Promise<Product[]> => {
    try {
      const { data, error } = await supabase
        .from(TABLE_PRODUCTS).select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map(item => mapProduct(item, true));
    } catch { return []; }
  },

  getProductAdmin: async (id: string): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from(TABLE_PRODUCTS).select('*').eq('id', id).single();
      if (error) throw error;
      return mapProduct(data, true);
    } catch { return null; }
  },

  saveProduct: async (product: Product): Promise<Product | null> => {
    try {
      const payload = {
        title: product.title, description: product.description,
        price: product.price, originalPrice: product.originalPrice,
        isOnSale: product.isOnSale, saleEndsAt: product.saleEndsAt,
        isNew: product.isNew, categories: product.categories,
        coverImage: product.coverImage, gallery: product.gallery,
        demoVideoUrl: product.demoVideoUrl,
        digitalAssetUrl: product.digitalAssetUrl,
        digitalAssetInstructions: product.digitalAssetInstructions,
        is18Plus: product.is18Plus, features: product.features, tags: product.tags
      };
      const { data, error } = await supabase
        .from(TABLE_PRODUCTS).insert([payload]).select().single();
      if (error) { console.error("Save Error:", error.message); return null; }
      return { ...product, id: data.id };
    } catch { return null; }
  },

  updateProduct: async (product: Product): Promise<boolean> => {
    try {
      const payload = {
        title: product.title, description: product.description,
        price: product.price, originalPrice: product.originalPrice,
        isOnSale: product.isOnSale, saleEndsAt: product.saleEndsAt,
        isNew: product.isNew, categories: product.categories,
        coverImage: product.coverImage, gallery: product.gallery,
        demoVideoUrl: product.demoVideoUrl,
        digitalAssetUrl: product.digitalAssetUrl,
        digitalAssetInstructions: product.digitalAssetInstructions,
        is18Plus: product.is18Plus, features: product.features, tags: product.tags
      };
      const { error } = await supabase.from(TABLE_PRODUCTS).update(payload).eq('id', product.id);
      if (error) { console.error("Update Error:", error.message); return false; }
      return true;
    } catch { return false; }
  },

  deleteProduct: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from(TABLE_PRODUCTS).delete().eq('id', id);
      return !error;
    } catch { return false; }
  },

  removeCategoryFromAllProducts: async (categoryToRemove: string): Promise<boolean> => {
    try {
      const { data: prods, error } = await supabase
        .from(TABLE_PRODUCTS).select('id, categories')
        .contains('categories', JSON.stringify([categoryToRemove]));
      if (error) throw error;
      if (!prods || prods.length === 0) return true;
      for (const p of prods) {
        const updated = (p.categories as string[]).filter(c => c !== categoryToRemove);
        await supabase.from(TABLE_PRODUCTS).update({ categories: updated }).eq('id', p.id);
      }
      return true;
    } catch { return false; }
  },

  // =============================================
  // SECURE DOWNLOADS (RPC — only for paid orders)
  // =============================================

  getDownloadLinks: async (invoiceId: number): Promise<DownloadLink[]> => {
    try {
      const { data, error } = await supabase.rpc('get_paid_downloads', { p_invoice_id: invoiceId });
      if (error) {
        console.warn("Download RPC unavailable:", error.message);
        return [];
      }
      return Array.isArray(data) ? data : (data || []);
    } catch { return []; }
  }
};
